/**
 * AutoCreateLauncher — v2-styled launcher for the AutoLet pipeline.
 *
 * Per user feedback ("AutoLets was autocreate from Runbooks, workflows,
 * URL etc"), this re-instates the v1 source-picker → upload → live-
 * wizard flow with the v2 design system, expanded to FOUR sources:
 *   - Runbook (PDF / DOCX / HTML upload — the original "recommended" path)
 *   - Existing Workflow (re-derive skills from a tenant workflow)
 *   - Public URL (scrape a public runbook page)
 *   - Process Library (RAG over the tenant's documents + spreadsheets)
 *
 * Stage map (mirrors v1 PHASE_DEFS):
 *   1. PARSE   — extract structured text from source
 *   2. INTENT  — LLM extracts trigger conditions + steps
 *   3. MATCH   — pgvector KNN against the Skill Registry
 *   4. GAPS    — surface unmatched steps for user resolution
 *   5. DRAFT   — assemble AgentDefinition JSON
 *   6. GUARDS  — compose guardrails + escalation chains
 *   7. REVIEW  — validator + human sign-off
 *
 * Once the backend returns a runId, hand off to the existing v1
 * `AutoCreateWizard` which owns the SSE event stream + gap-resolution
 * + agent-def review screens. That wizard is identity-stable across
 * v1 and v2 — no need to fork it.
 */
import React, { useEffect, useState } from 'react';
import {
  BookOpen, GitBranch, FileText, FileSpreadsheet, ChevronRight, Upload,
  ArrowLeft, Sparkles, X,
} from 'lucide-react';
import { autoCreateAgent, stageAutoCreateRunbook, getWorkflows } from '../../services/api';
import { validateExternalUrl } from '../../utils/validateExternalUrl';
import { isUrlAllowed, getUrlAllowlist } from '../../utils/urlAllowlist';
import { useAuth } from '../../contexts/AuthContext';
import useWizardDraft from '../../hooks/useWizardDraft';
import AutoCreateWizard from '../autocreate/AutoCreateWizard';

/* Stable ref so useWizardDraft's memoized callbacks don't churn each render.
 * `name` gives the central Drafts list a readable label from this flow's own field. */
const AUTOLET_DRAFT_SERVER = { archetype: 'autolet', name: (snap) => snap?.form?.agentName || null };
/* Phase 5B — the launcher used to host a `<Modal>` per phase; after
 * Phase 1A grew the upload form past modal-shape (four mode pickers
 * + advisory + per-source input) the V2 G378 pattern says this is
 * slideout-shaped. Keep the SourceModal / UploadModal function names
 * for now (renaming would create unnecessary diff noise) but each
 * renders a Slideout inside. The wizard hand-off at phase='wizard'
 * mounts AutoCreateWizard unchanged — that component owns its own
 * shell (modal + SSE feed). */
import { Slideout } from './Slideout';
import { Button } from './Button';
import { Pill } from './Pill';

const SOURCES = [
  {
    kind: 'runbook',
    title: 'From a Runbook',
    description: 'Upload a Word, PDF, or HTML runbook. The pipeline extracts steps, links them to Skill Registry entries, and emits a fully protected AutoLet — pre-bound and ready for your review.',
    icon: BookOpen,
    badge: 'Recommended',
    accent: 'cyan',
  },
  {
    kind: 'workflow',
    title: 'From an Existing Workflow',
    description: 'Pick one of your tenant\'s active workflows. The pipeline re-derives skills against the current Skill Registry and attaches them to a fresh agent stub.',
    icon: GitBranch,
    accent: 'trust',
  },
  /* 'url' (From a Public URL) source is HIDDEN. The backend is not wired —
   * there is no SOURCE_URL kind, no server-side fetcher/sanitizer, and no real
   * allowlist persistence (the FE allowlist is localStorage-only). Parked as a
   * product change request for later roadmap consideration. The downstream
   * url-handling branches in UploadModal are retained (now unreachable, since the
   * card is the only way to select sourceKind 'url'), so re-enabling is a
   * low-effort revert once the BE lands. */
  {
    kind: 'process_files',
    title: 'From the Process Library',
    description: 'Upload one or more documents or spreadsheets (PDF, DOCX, TXT, MD, XLSX). The pipeline extracts steps, links them to Skill Registry entries, and emits a fully protected AutoLet.',
    icon: FileText,
    accent: 'warning',
  },
];

/* ════════════════════════════════════════════════════════════════════
 * The agent-typing model is five orthogonal dimensions. The old
 * single AGENT_TYPES dropdown conflated four of them and offered
 * `cognitive` / `autonomous` as domain values — both of which
 * actually belong to OTHER dimensions:
 *   - `cognitive`  is an EXECUTION_MODE  (how it reasons)
 *   - `autonomous` is an OPERATION_MODE  (who has authority)
 * The corrected model — every value belongs to exactly one column,
 * enforced by V188 CHECK constraints:
 *
 *   FUNCTION_MODES  — what discipline the agent serves
 *   INDUSTRY_MODES  — what vertical it operates in
 *   OPERATION_MODES — who has authority to act on its behalf
 *   EXECUTION_MODES — how it reasons at runtime
 *
 * Trust band is the fifth dimension but is platform-written (score
 * waterfall), never user-pickable at creation. */
const FUNCTION_MODES = [
  { value: 'compliance', label: 'compliance — evidence collection' },
  { value: 'threat',     label: 'threat — SOC / IR triage' },
  { value: 'iam',        label: 'iam — access governance' },
  { value: 'appsec',     label: 'appsec — code + dependency review' },
  { value: 'devops',     label: 'devops — platform engineering, infra automation' },
  { value: 'secops',     label: 'secops — security operations, detection engineering' },
  { value: 'general',    label: 'general — catch-all / cross-functional' },
];

const INDUSTRY_MODES = [
  { value: 'general',       label: 'general — industry-agnostic' },
  { value: 'financial',     label: 'financial — banking, capital markets, payments' },
  { value: 'insurance',     label: 'insurance — P&C, life, health, reinsurance' },
  { value: 'healthcare',    label: 'healthcare — providers, payers, HIPAA-bound' },
  { value: 'retail',        label: 'retail — commerce, supply chain' },
  { value: 'telco',         label: 'telco — carriers, network operators' },
  { value: 'government',    label: 'government — public sector, regulated agency' },
  { value: 'energy',        label: 'energy — utilities, oil & gas, NERC-CIP' },
  { value: 'manufacturing', label: 'manufacturing — industrial, OT/ICS' },
];

const OPERATION_MODES = [
  { value: 'platform',   label: 'platform — platform-orchestrated, no human in loop' },
  { value: 'human',      label: 'human — every action gated by human approval' },
  { value: 'autonomous', label: 'autonomous — self-directed within policy (requires Full trust)' },
];

const EXECUTION_MODES = [
  { value: 'auto',          label: 'auto — recommended (reasoning agent for documents, workflow-driven for imports)' },
  { value: 'deterministic', label: 'deterministic — workflow-driven, LLM only at skill boundaries' },
  { value: 'cognitive',     label: 'cognitive — LLM-driven reasoning loop with tool catalog' },
  { value: 'hybrid',        label: 'hybrid — deterministic head/tail around a cognitive body' },
];

/* ════════════════════════════════════════════════════════════════════════
   <AutoCreateLauncher /> — top-level driver
   Three internal phases:
     'pick'     → SourceModal
     'upload'   → UploadModal (carries the selected sourceKind)
     'wizard'   → mounts the existing v1 AutoCreateWizard with the runId
   ════════════════════════════════════════════════════════════════════════ */
export default function AutoCreateLauncher({ isOpen, onClose, onCreated, seedFromDraft }) {
  const [phase, setPhase] = useState('pick');
  const [sourceKind, setSourceKind] = useState(null);
  const [runId, setRunId] = useState(null);

  // Reset state every time the launcher opens. When resuming a draft from the central
  // Drafts panel, jump straight to the upload step for the draft's source kind.
  useEffect(() => {
    if (isOpen) {
      const seededKind = seedFromDraft?.form?.form?.sourceKind || null;
      if (seededKind) {
        setSourceKind(seededKind);
        setPhase('upload');
      } else {
        setPhase('pick');
        setSourceKind(null);
      }
      setRunId(null);
    }
  }, [isOpen, seedFromDraft]);

  if (!isOpen) return null;

  const closeAll = () => {
    setPhase('pick');
    setSourceKind(null);
    setRunId(null);
    onClose?.();
  };

  if (phase === 'wizard' && runId) {
    return (
      <AutoCreateWizard
        runId={runId}
        onClose={() => {
          // Wizard owns the post-commit refresh signal; onCreated lets
          // the parent inventory page reload its agents list.
          onCreated?.();
          closeAll();
        }}
      />
    );
  }

  if (phase === 'upload' && sourceKind) {
    return (
      <UploadModal
        sourceKind={sourceKind}
        resumeDraft={seedFromDraft}
        onBack={() => { setSourceKind(null); setPhase('pick'); }}
        onClose={closeAll}
        onRunStarted={(id) => { setRunId(id); setPhase('wizard'); }}
      />
    );
  }

  return (
    <SourceModal
      onPick={(kind) => { setSourceKind(kind); setPhase('upload'); }}
      onClose={closeAll}
    />
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SourceModal — pick one of the 4 source kinds.
   ════════════════════════════════════════════════════════════════════════ */
function SourceModal({ onPick, onClose }) {
  return (
    <Slideout
      isOpen={true}
      onClose={onClose}
      width={640}
      ariaLabel="Auto-create AutoLet — pick a source"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Pill tone="cyan" size="sm">AutoLet</Pill>
            <span className="text-[11px] text-v2-ink-muted">Step 1 of 2 — Source</span>
          </div>
          <h2 className="text-[16px] font-bold m-0">Auto-create AutoLet — pick a source</h2>
        </div>
      }
      footer={<div className="flex items-center justify-end w-full"><Button variant="secondary" onClick={onClose}>Cancel</Button></div>}
    >
      <p className="text-[13px] text-v2-ink-secondary leading-relaxed mb-4">
        Auto-create takes you from a source artefact to a fully protected AutoLet
        in one continuous pipeline — extracted skills land in the Build phase
        pre-bound to Skill Registry entries, ready for your review and probation
        tuning.
      </p>
      <div className="space-y-2">
        {SOURCES.map((src) => {
          const Icon = src.icon;
          return (
            <button
              key={src.kind}
              type="button"
              onClick={() => onPick(src.kind)}
              className="v2-focus-ring w-full text-left bg-v2-surface border border-v2-border-subtle hover:border-v2-action hover:bg-v2-action-soft rounded-v2-md px-4 py-3 flex items-start gap-3 transition-colors duration-v2"
            >
              <div className="w-9 h-9 rounded-v2-sm bg-v2-action-soft text-v2-action flex items-center justify-center flex-shrink-0">
                <Icon size={16} aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h4 className="text-[13.5px] font-semibold text-v2-ink m-0">{src.title}</h4>
                  {src.badge && <Pill tone={src.accent} size="sm">{src.badge}</Pill>}
                </div>
                <p className="text-[12px] text-v2-ink-secondary leading-relaxed m-0">{src.description}</p>
              </div>
              <ChevronRight size={14} className="text-v2-ink-muted mt-3 flex-shrink-0" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </Slideout>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   UploadModal — collect the source-specific input + common hints,
   POST /api/v1/auto-create, hand the runId up to the launcher.
   ════════════════════════════════════════════════════════════════════════ */
function UploadModal({ sourceKind, resumeDraft, onBack, onClose, onRunStarted }) {
  const src = SOURCES.find((s) => s.kind === sourceKind) || SOURCES[0];
  const Icon = src.icon;
  const { session } = useAuth();
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  const [sourceRef, setSourceRef] = useState('');
  const [agentName, setAgentName] = useState('');
  /* Four-dimension picker state. Defaults are the safest possible
   * combination: general/general/platform/deterministic. The user
   * MUST pick at least function_mode + execution_mode before
   * submission — they describe what the agent is for and how it
   * reasons; defaults aren't a reasonable substitute. The backend
   * V188 CHECK constraints reject any cross-mode combination that
   * violates the invariant matrix (e.g. operation_mode=autonomous
   * requires trust_band=Full, which a fresh agent never has). */
  const [functionMode,  setFunctionMode]  = useState('general');
  const [industryMode,  setIndustryMode]  = useState('general');
  const [operationMode, setOperationMode] = useState('platform');
  // 'auto' defers to the backend's smart default (cognitive for prose/spreadsheet
  // sources that carry decision logic; deterministic for imported workflows).
  const [executionMode, setExecutionMode] = useState('auto');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  /* Process Library source — inline multi-file upload (mirrors the Runbook
   * dropzone). Each file is staged via the upload endpoint with
   * purpose=process_files; sourceRef carries the comma-joined staging refs the
   * process_files pipeline consumes. No pre-existing agent/skill required. */
  const [uploadedFiles, setUploadedFiles] = useState([]); // [{ ref, filename, isSpreadsheet }]
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const handleProcessFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file later
    if (files.length === 0) return;
    setError(null);
    setUploadingFiles(true);
    try {
      const staged = [];
      for (const f of files) {
        const res = await stageAutoCreateRunbook(f, 'process_files');
        if (!res || !res.stagingId) throw new Error(`Upload failed for ${f.name}`);
        staged.push({
          ref: res.stagingId,
          filename: res.filename || f.name,
          isSpreadsheet: /\.(xlsx|xls|csv)$/i.test(f.name),
        });
      }
      const next = [...uploadedFiles, ...staged];
      setUploadedFiles(next);
      setSourceRef(next.map((s) => s.ref).join(','));
    } catch (err) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setUploadingFiles(false);
    }
  };
  const removeUploadedFile = (ref) => {
    const next = uploadedFiles.filter((s) => s.ref !== ref);
    setUploadedFiles(next);
    setSourceRef(next.map((s) => s.ref).join(','));
  };

  /* ── Drafts (V331) — persist the pre-run config so the operator can leave and resume.
   * Two-tier via useWizardDraft: localStorage (instant) + server (durable / central
   * Drafts list). No secrets in this config, so nothing is excluded. ─────────────── */
  const { save: saveDraft, clear: clearDraft, adoptServerDraft } =
    useWizardDraft('prove7:autolet-wizard-draft', { server: AUTOLET_DRAFT_SERVER });
  const [draftHydrated, setDraftHydrated] = useState(false);

  // Restore once on mount, but ONLY from an explicit panel resume (the central Drafts
  // list is the resume hub) — never silently resurrect a local draft on a fresh source
  // pick, which would surprise someone creating a brand-new AutoLet. Autosave still
  // persists to the server so the draft always appears in the panel.
  useEffect(() => {
    if (draftHydrated) return;
    const panelSnap = (resumeDraft && resumeDraft.form && resumeDraft.form.form) ? resumeDraft.form : null;
    if (panelSnap && resumeDraft.id) adoptServerDraft(resumeDraft.id);
    const cfg = panelSnap?.form;
    if (cfg && cfg.sourceKind === sourceKind) {
      if (typeof cfg.sourceRef === 'string') setSourceRef(cfg.sourceRef);
      if (typeof cfg.agentName === 'string') setAgentName(cfg.agentName);
      if (cfg.functionMode) setFunctionMode(cfg.functionMode);
      if (cfg.industryMode) setIndustryMode(cfg.industryMode);
      if (cfg.operationMode) setOperationMode(cfg.operationMode);
      if (cfg.executionMode) setExecutionMode(cfg.executionMode);
      if (Array.isArray(cfg.uploadedFiles)) setUploadedFiles(cfg.uploadedFiles);
    }
    setDraftHydrated(true);
  }, [draftHydrated, resumeDraft, adoptServerDraft, sourceKind]);

  // Auto-save the config after hydration (the hook debounces the server write-through).
  useEffect(() => {
    if (!draftHydrated) return;
    saveDraft({ form: {
      sourceKind, sourceRef, agentName, functionMode, industryMode, operationMode, executionMode, uploadedFiles,
    } });
  }, [draftHydrated, saveDraft, sourceKind, sourceRef, agentName,
      functionMode, industryMode, operationMode, executionMode, uploadedFiles]);

  /* G70 — URL allowlist gate. Read once when the modal mounts (not in
   * an effect — we want the first render to know if URL submissions
   * are open or closed for this tenant). */
  const allowlist = sourceKind === 'url' ? getUrlAllowlist(orgId, tenantId) : [];
  const allowlistConfigured = allowlist.length > 0;

  /* G72 — Workflow picker scope. v1's PlatformContext fetches workflows
   * with `{ orgId, tenantId, archived: 'all' }`. The empty-scope call
   * the launcher used previously returned 0 rows even when the canvas
   * dropdown showed 60 — backend defaults to a stricter filter for
   * empty scope. Pass the session ids + archived='all' (read-only
   * picker, soft-deleted workflows are never useful as auto-create
   * sources but the v1 PlatformContext used 'all' so we mirror it). */
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  useEffect(() => {
    if (sourceKind !== 'workflow') return;
    let cancelled = false;
    setLoadingWorkflows(true);
    // RC-19f (task #210) — AutoLet creation only accepts STANDALONE
    // workflows as sources. Pass standaloneOnly=true so the BE filters
    // out AutoLet CEGs (auto-derived runbooks already bound to an
    // existing AutoLet agent, persisted with is_standalone=false per
    // ADR-001 §5.2 / V238). Without this filter the dropdown listed
    // "AutoLet CEG · <agent> · v1" entries — non-sensical sources for
    // a fresh AutoLet, since they already belong to another agent.
    Promise.resolve(getWorkflows({ orgId, tenantId, archived: 'all', standaloneOnly: true }))
      .then((res) => {
        if (cancelled) return;
        const arr = res?.ok && Array.isArray(res.data) ? res.data
          : Array.isArray(res?.data?.workflows) ? res.data.workflows
          : Array.isArray(res) ? res : [];
        /* Mirror v1 label shape: "<name> · v<version>" or
         * "<name> · <versionLabel>". Fallback chain matches v1
         * AgentsPage.jsx line 5832-5837. */
        setWorkflowOptions(arr.map((w) => ({
          id: w.id,
          name: w.name || w.workflowName || w.id,
          version: w.version,
          versionLabel: w.versionLabel,
        })));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingWorkflows(false); });
    return () => { cancelled = true; };
  }, [sourceKind, orgId, tenantId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFileName(file.name);
    setUploading(true);
    try {
      /* G73 — read `res.stagingId`. The runbook-upload endpoint is
       * an exception to the apiFetch envelope rule (it uses `fetch()`
       * directly to support multipart) so the response is the raw
       * JSON body, NOT wrapped in `{ok, data}`. v1 contract from
       * pages/AgentsPage.jsx line 5480-5486:
       *   const res = await stageAutoCreateRunbook(file);
       *   if (!res || !res.stagingId) throw …;
       *   setSourceRef(res.stagingId); // 'upload://staging/<uuid>'
       *
       * Round 11's first cut read `res.sourceRef || res.ref || res.id`
       * which never matched, so every upload reported "Upload returned
       * no source reference" even when the upload succeeded. */
      const res = await stageAutoCreateRunbook(file);
      if (!res || !res.stagingId) {
        throw new Error('Upload acknowledged but server returned no stagingId.');
      }
      setSourceRef(res.stagingId);
      if (!agentName) setAgentName(file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setError(err?.message || 'Upload failed.');
      setFileName('');
    } finally {
      setUploading(false);
    }
  };

  /* G70 hardening — URL source is double-gated:
   *   1. Structural / SSRF check via validateExternalUrl (utils):
   *      blocks unsafe schemes, private/loopback/metadata/link-local
   *      ranges, embedded credentials, internal pseudo-TLDs, etc.
   *   2. Per-tenant allowlist check via isUrlAllowed (utils): the
   *      Super Admin must have explicitly approved the URL pattern
   *      for THIS org/tenant. If the allowlist is empty, URL source
   *      is closed entirely.
   * Backend MUST run the equivalent gate — assume any client check
   * can be bypassed. */
  const urlValidation = sourceKind === 'url' ? validateExternalUrl(sourceRef) : null;
  const urlAllowlistCheck = sourceKind === 'url' && urlValidation?.ok
    ? isUrlAllowed(urlValidation.normalized, orgId, tenantId)
    : null;
  const urlInvalid = sourceKind === 'url' && sourceRef.trim().length > 0 && urlValidation && !urlValidation.ok;
  const urlNotAllowlisted = sourceKind === 'url' && urlValidation?.ok && urlAllowlistCheck && !urlAllowlistCheck.ok;

  const canSubmit = !submitting && !uploading && sourceRef.trim().length > 0
    && (sourceKind !== 'url' || (urlValidation?.ok && urlAllowlistCheck?.ok));

  const handleSubmit = async () => {
    /* Defense in depth — even if button-state checks are tampered
     * with, refuse to POST a URL that fails either gate. */
    if (sourceKind === 'url') {
      const v = validateExternalUrl(sourceRef);
      if (!v.ok) {
        setError(v.reason || 'URL failed validation.');
        return;
      }
      const a = isUrlAllowed(v.normalized, orgId, tenantId);
      if (!a.ok) {
        setError(a.reason || 'URL is not allowed for this tenant.');
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const hints = {};
      if (agentName)     hints.proposedAgentName = agentName.trim();
      /* Five-dimension agent typing — every key maps to a column V188
       * adds with a CHECK constraint. The backend will reject the
       * AutoCreate run if any value is outside its enum, so we
       * surface the four pickers explicitly rather than leaving the
       * pipeline to invent defaults. */
      hints.function_mode  = functionMode;
      hints.industry_mode  = industryMode;
      hints.operation_mode = operationMode;
      // Only pin execution_mode when the operator explicitly overrides 'auto';
      // omitting it lets the backend apply its source-aware smart default.
      if (executionMode && executionMode !== 'auto') hints.execution_mode = executionMode;
      const safeRef = sourceKind === 'url' && urlValidation?.ok ? urlValidation.normalized : sourceRef.trim();
      const res = await autoCreateAgent({
        sourceKind,
        sourceRef: safeRef,
        proposedAgentName: agentName ? agentName.trim() : undefined,
        hints: Object.keys(hints).length > 0 ? hints : undefined,
      });
      const payload = res?.ok ? res.data : res;
      if (payload?.runId) {
        clearDraft(); // pipeline started — the run is the source of truth now
        onRunStarted?.(payload.runId);
        return;
      }
      if (payload?.agentId) {
        // Legacy synchronous-success path.
        clearDraft();
        onRunStarted?.(payload.runId || null);
        return;
      }
      throw new Error(payload?.message || 'Server returned no runId — pipeline did not start.');
    } catch (err) {
      setError(err?.message || 'Auto Create failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slideout
      isOpen={true}
      onClose={submitting ? undefined : onClose}
      width={720}
      ariaLabel={`Auto-create — ${src.title}`}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Pill tone="cyan" size="sm">AutoLet</Pill>
            <span className="text-[11px] text-v2-ink-muted">Step 2 of 2 — Configure</span>
          </div>
          <h2 className="text-[16px] font-bold m-0">Auto-create — {src.title}</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1 mb-0">
            Configure the source input + agent typing. The pipeline starts once you click Begin Auto Creation.
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onBack} disabled={submitting} iconLeft={<ArrowLeft size={13} />}>Back</Button>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit} iconLeft={<Sparkles size={13} />}>
            {submitting ? 'Starting pipeline…' : 'Begin Auto Creation'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 px-3 py-2.5 rounded-v2-md bg-v2-action-soft border border-v2-action">
          <div className="w-8 h-8 rounded-v2-sm bg-v2-action text-white flex items-center justify-center flex-shrink-0">
            <Icon size={15} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-v2-action-strong">{src.title}</div>
            <div className="text-[11.5px] text-v2-ink-secondary leading-relaxed mt-0.5">{src.description}</div>
          </div>
        </div>

        {error && (
          <div role="alert" className="px-3 py-2 rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger text-[12px]">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* ── Source-specific input ───────────────────────────── */}
        {sourceKind === 'runbook' && (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Runbook file (PDF, DOCX, HTML)</label>
            <label className="v2-focus-ring flex items-center justify-center gap-2 w-full px-3 py-6 border-2 border-dashed border-v2-border rounded-v2-md text-v2-ink-secondary hover:border-v2-action hover:bg-v2-action-soft cursor-pointer transition-colors duration-v2">
              <Upload size={16} aria-hidden="true" />
              {uploading ? 'Uploading…' : (fileName || 'Click to choose a file')}
              <input type="file" className="sr-only" accept=".pdf,.doc,.docx,.html,.htm,.md,.txt" onChange={handleFileChange} disabled={uploading || submitting} />
            </label>
            {sourceRef && !uploading && (
              <div className="text-[11px] text-v2-success mt-1.5">✓ Uploaded · ref <code className="font-mono-v2">{sourceRef.slice(0, 28)}…</code></div>
            )}
          </div>
        )}

        {sourceKind === 'workflow' && (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Existing workflow *</label>
            {loadingWorkflows ? (
              <div className="text-[12px] text-v2-ink-muted px-2 py-2">Loading workflows…</div>
            ) : workflowOptions.length === 0 ? (
              <div className="px-3 py-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary">
                No workflows visible in this scope. Build one in <strong>Workflow Canvas</strong> first, or check that your tenant has workflows in the <code className="font-mono-v2">archived: all</code> filter.
              </div>
            ) : (
              <select
                value={sourceRef}
                onChange={(e) => setSourceRef(e.target.value)}
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
              >
                <option value="">Select a workflow…</option>
                {workflowOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.versionLabel ? ` · ${w.versionLabel}` : (w.version ? ` · v${w.version}` : '')}
                  </option>
                ))}
              </select>
            )}
            <div className="text-[11px] text-v2-ink-muted mt-1">{workflowOptions.length} workflow{workflowOptions.length === 1 ? '' : 's'} found in scope orgId={orgId ? '✓' : '—'} tenantId={tenantId ? '✓' : '—'}.</div>
          </div>
        )}

        {sourceKind === 'url' && (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Public runbook URL</label>
            {/* G70 — Super-Admin allowlist gate. URL source is closed
              * by default for every tenant; the Super Admin must
              * explicitly enumerate the URL patterns the platform is
              * allowed to fetch. If the list is empty, lock the input
              * and tell the user where to enable it. */}
            {!allowlistConfigured ? (
              <div className="px-3 py-3 rounded-v2-sm border border-v2-warning bg-v2-warning-soft text-[12px] leading-relaxed">
                <div className="font-semibold text-v2-warning mb-1">URL source disabled — no allowlist configured</div>
                <div className="text-v2-ink-secondary">
                  AutoCreate-from-URL is locked for this tenant. A <strong>Super Admin</strong> must approve URL patterns in <strong>Admin Center → URL Allowlist</strong> before this source can be used. Random URLs are not accepted by design — only patterns explicitly authorised for your org/tenant.
                </div>
              </div>
            ) : (
              <>
                <input
                  type="url"
                  value={sourceRef}
                  onChange={(e) => setSourceRef(e.target.value)}
                  placeholder="https://docs.example.com/runbook/incident-response"
                  className={'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border bg-v2-surface text-v2-ink font-mono-v2 text-[12px] ' + (
                    urlInvalid ? 'border-v2-danger'
                    : urlNotAllowlisted ? 'border-v2-warning'
                    : urlValidation?.ok ? 'border-v2-success'
                    : 'border-v2-border')}
                />
                <div className="text-[11px] text-v2-ink-muted mt-1">
                  HTTPS-only · {allowlist.length} pattern{allowlist.length === 1 ? '' : 's'} approved · public pages only · auth-protected URLs must be uploaded as runbook files.
                </div>
                {urlInvalid && (
                  <div role="alert" className="mt-2 px-2.5 py-1.5 rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger text-[11.5px]">
                    <strong>Validation failed:</strong> {urlValidation.reason}
                  </div>
                )}
                {urlNotAllowlisted && (
                  <div role="alert" className="mt-2 px-2.5 py-1.5 rounded-v2-sm border border-v2-warning bg-v2-warning-soft text-v2-warning text-[11.5px]">
                    <strong>Not in allowlist:</strong> {urlAllowlistCheck.reason}
                  </div>
                )}
                {urlValidation?.ok && urlAllowlistCheck?.ok && (
                  <div className="mt-2 px-2.5 py-1.5 rounded-v2-sm border border-v2-success bg-v2-success-soft text-v2-success text-[11.5px]">
                    ✓ URL passes structural validation and matches an approved allowlist pattern.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {sourceKind === 'process_files' && (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Process files (PDF, DOCX, TXT, MD, XLSX)</label>
            {/* Inline multi-file dropzone — mirrors the Runbook upload. Each file
              * stages on selection; sourceRef = comma-joined staging refs. */}
            <label className="v2-focus-ring flex items-center justify-center gap-2 w-full px-3 py-6 border-2 border-dashed border-v2-border rounded-v2-md text-v2-ink-secondary hover:border-v2-action hover:bg-v2-action-soft cursor-pointer transition-colors duration-v2">
              <Upload size={16} aria-hidden="true" />
              {uploadingFiles ? 'Uploading…' : 'Click to choose files'}
              <input
                type="file"
                multiple
                className="sr-only"
                accept=".pdf,.doc,.docx,.html,.htm,.md,.txt,.xlsx"
                onChange={handleProcessFilesChange}
                disabled={uploadingFiles || submitting}
              />
            </label>
            {uploadedFiles.length > 0 && (
              <div className="space-y-1.5 mt-2">
                {uploadedFiles.map((s) => (
                  <div key={s.ref} className="flex items-center gap-2.5 rounded-v2-sm border border-v2-border bg-v2-surface px-2.5 py-2">
                    {s.isSpreadsheet
                      ? <FileSpreadsheet size={15} className="text-v2-success shrink-0" aria-hidden="true" />
                      : <FileText size={15} className="text-v2-ink-muted shrink-0" aria-hidden="true" />}
                    <span className="flex-1 min-w-0 text-[12.5px] font-medium text-v2-ink truncate">{s.filename}</span>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(s.ref)}
                      aria-label={`Remove ${s.filename}`}
                      className="v2-focus-ring text-v2-ink-muted hover:text-v2-danger p-0.5 rounded-v2-sm"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-v2-ink-muted leading-relaxed mt-1.5">
              Upload one or more documents or spreadsheets. The pipeline extracts steps, links them to Skill Registry entries, and emits a fully protected AutoLet for your review.
            </p>
          </div>
        )}

        {/* ── Common metadata hints ───────────────────────────── */}
        <div className="border-t border-v2-border-subtle pt-4 space-y-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted">Optional pipeline hints</div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Proposed agent name <span className="text-v2-ink-muted font-normal normal-case tracking-normal">(optional)</span></label>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="The pipeline will derive a name if you leave this blank"
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
            />
            {/* G75 — mirror v1's AutoLets- prefix preview so the user
              * understands the naming convention. Backend defensively
              * normalises (always prepends AutoLets- if absent), but
              * showing the final shape avoids surprise. */}
            <div className="text-[11px] text-v2-ink-muted mt-1.5">
              Final agent name:{' '}
              <code className="font-mono-v2 text-[11px] bg-v2-action-soft text-v2-action-strong border border-v2-action rounded-v2-sm px-1.5 py-0.5">
                {agentName && agentName.trim()
                  ? (agentName.trim().startsWith('AutoLets-') ? agentName.trim() : `AutoLets-${agentName.trim()}`)
                  : 'AutoLets-<derived from source>'}
              </code>
            </div>
          </div>
          {/* Row 1 — Function + Industry. Both required, both backed
              by a CHECK enum in V188. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                Function mode <span className="text-v2-ink-secondary font-normal normal-case">— what discipline</span>
              </label>
              <select
                value={functionMode}
                onChange={(e) => setFunctionMode(e.target.value)}
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
              >
                {FUNCTION_MODES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                Industry mode <span className="text-v2-ink-secondary font-normal normal-case">— what vertical</span>
              </label>
              <select
                value={industryMode}
                onChange={(e) => setIndustryMode(e.target.value)}
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
              >
                {INDUSTRY_MODES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {/* Row 2 — Operation + Execution. The cross-mode invariant
              matrix (V188) constrains which combinations are legal:
                operation_mode=autonomous → trust_band must be Full
                  (fresh agents start at Supervised, so autonomous is
                   effectively disabled at creation time — operator
                   promotes once the agent earns Full trust)
                operation_mode=human       → trust_band ≠ Full
              The picker accepts any selection; the backend rejects
              illegal combinations with a named-CHECK error. */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                Operation mode <span className="text-v2-ink-secondary font-normal normal-case">— who has authority</span>
              </label>
              <select
                value={operationMode}
                onChange={(e) => setOperationMode(e.target.value)}
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
              >
                {OPERATION_MODES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                Execution mode <span className="text-v2-ink-secondary font-normal normal-case">— how it reasons</span>
              </label>
              <select
                value={executionMode}
                onChange={(e) => setExecutionMode(e.target.value)}
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
              >
                {EXECUTION_MODES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {/* Cross-mode advisory — surfaces the most common invariant
              violation BEFORE the user hits submit. The full matrix
              is enforced server-side, but a fresh agent literally
              cannot be autonomous (no Full trust band yet) so we
              call that out here. */}
          {operationMode === 'autonomous' && (
            <div className="mt-2 text-[11px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2">
              <strong>Heads up:</strong> autonomous operation requires a Full trust band. Fresh agents start at Supervised, so this combination will be rejected at creation. Pick <code>platform</code> or <code>human</code> now and promote later via the trust ledger once the agent has earned Full trust.
            </div>
          )}
        </div>

        <div className="text-[11.5px] text-v2-ink-muted leading-relaxed bg-v2-muted border border-v2-border-subtle rounded-v2-sm px-3 py-2">
          <strong className="text-v2-ink">7-stage pipeline:</strong> PARSE → INTENT → MATCH → GAPS → DRAFT → GUARDS → REVIEW. You'll see live phase chips + an event feed once Begin Auto Creation kicks off. Capability gaps and the final agent definition both pause for your review.
        </div>
      </div>
    </Slideout>
  );
}
